<template>
  <LydiaShortageModal
    v-model="showLydiaShortageModal"
    @confirm="handleModalConfirm"
    @charge="handleModalCharge"
  />

  <div class="setup-container">
    <h1 class="page-title">사주 정보</h1>

    <p v-if="userGanji === '모름'" class="guide-text">
      내 사주 정보를 저장하시면, 최초 1회 3,000 LYD가 지급됩니다.
    </p>
      <!-- 내 사주로 진행 -->
    <label class="radio-label">
      <input type="radio" v-model="mode" value="my" :disabled="!hasBirth" />
      <span>내 사주로 진행</span>
    </label>
    <div class="section">
      <div
        class="my-info-box"
        :class="{ disabled: mode !== 'my' }"
      >
        <p class="info-text">사주: {{ userGanji }}<br></br>체질: {{ userBodyType }}</p>
        <div v-if="userBodyType === '모름' && userGanji !== '모름'">
          <button class="menu-button" @click="startBodyTypeAnalysis">체질 분석 진행하기</button>
          <p class="hint">[체질을 알면 더 상세한 결과를 받으실 수 있습니다.]</p>
          <div class="bodytype-wrapper">
            <div v-if="isBodyTypeLoading" class="info-text">로딩 중...</div>
            <BodyTypes
              v-if="showBodyType"
              :showQuestions="true"
              @body-type-calculated="onBodyTypeCalculated"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- 새 사주로 진행 -->
    <label class="radio-label">
      <input type="radio" v-model="mode" value="new" />
      <span>새 사주로 진행</span>
    </label>
    <div class="section">
      <div class="info-box" :class="{ disabled: mode !== 'new' }">
        <input
          class="input"
          v-model="name"
          type="text"
          placeholder="이름 (선택 사항)"
          @input="validateName"
          :disabled="mode !== 'new'"
        />
        <div v-if="nameError" class="error-text">{{ nameError }}</div>

        <!-- 성별 선택 -->
        <div class="gender-options">
          <label class="gender-label">
            <input
              type="radio"
              value="남성"
              v-model="gender"
              :disabled="mode !== 'new'"
            />
            남성
          </label>
          <label class="gender-label">
            <input
              type="radio"
              value="여성"
              v-model="gender"
              :disabled="mode !== 'new'"
            />
            여성
          </label>
        </div>
        <div v-if="genderError" class="error-text">
          성별을 선택해 주세요.
        </div>

        <input
          class="input"
          v-model="birthDate"
          type="text"
          placeholder="생년월일(YYYYMMDD)"
          inputmode="numeric"
          maxlength="8"
          @input="validateBirthDate"
          :disabled="mode !== 'new'"
        />
        <div v-if="birthDateError" class="error-text">{{ birthDateError }}</div>
        <div v-else-if="!birthDate && submitted" class="error-text">
          생년월일을 입력해 주세요.
        </div>

        <!-- 달력 선택 -->
        <div class="calendar-options">
          <label class="calendar-label">
            <input
              type="checkbox"
              v-model="isLunar"
              :disabled="mode !== 'new'"
            />
            음력
          </label>

          <label class="calendar-label">
            <input
              type="checkbox"
              v-model="isLeapMonth"
              :disabled="mode !== 'new' || !isLunar"
            />
            윤달
          </label>
        </div>

        <select class="input" v-model="birthTime" :disabled="mode !== 'new'">
          <option disabled value="">태어난 시간</option>
          <option v-for="hour in timeOptions" :key="hour" :value="hour">
            {{ hour }}
          </option>
        </select>
        <div v-if="!birthTime && submitted" class="error-text">
          태어난 시간을 선택해 주세요.
        </div>

        <input
          class="input"
          v-model="email"
          type="text"
          placeholder="이메일 (선택 사항)"
          inputmode="email"
          @input="validateEmail"
          :disabled="mode !== 'new'"
        />
        <div v-if="emailError" class="error-text">{{ emailError }}</div>

        <label class="checkbox-label">
          <input
            type="checkbox"
            v-model="saveAsMyInfo"
            :disabled="mode !== 'new'"
          />
          <span>내 사주 정보로 저장</span>
        </label>
      </div>
    </div>

    <div class="button-group">
      <button class="primary-button" @click="handleSubmit" :disabled="loading">
        분석요청
      </button>
      <button class="back-button" @click="goBack">돌아가기</button>
    </div>

    <!-- 복구 코드 모달 -->
    <div v-if="showRecoveryCodeModal" class="modal-overlay">
      <div class="modal-content">
        <h2>정보 저장 완료</h2>
        <p>
          정보가 성공적으로 저장되었습니다.
          <br />
          ({{ welcomeLydia }} LYD가 지급되었습니다.)
        </p>
        <p>
          아래 복구 코드를 반드시 기록해두세요.
          <br />
          계정 복구 시 필요합니다.
        </p>
        <p class="recovery-code">{{ recoveryCode }}</p>
        <button class="modal-button" @click="closeRecoveryCodeModal">
          확인
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, inject, onMounted } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useStore } from 'vuex'
  import { doc, setDoc, updateDoc } from 'firebase/firestore'
  import { db } from '@/firebase'
  import { gql, GraphQLClient } from 'graphql-request'
  import { generateCode } from '@/utils/utils.js'
  import BodyTypes from '@/components/BodyTypes.vue'
  import LydiaShortageModal from '@/components/LydiaShortageModal.vue'
  import { canUseThis } from '@/utils/checkLydia'

  const router = useRouter()
  const route = useRoute()
  const store = useStore()
  const showToast = inject('showToast')

  const user = computed(() => store.getters.getUser)
  const userGanji = computed(() => {
    const ganji = user.value?.ganji
    if (!ganji) return '모름'
    return `${ganji.yearGanji}(년), ${ganji.monthGanji}(월), ${ganji.dayGanji}(일), ${ganji.hourGanji}(시)`
  })

  const userBodyType = computed(() => {
    return bodyTypeResult.value || user.value?.bodyType || '모름'
  })
  const hasBirth = computed(() => !!user.value?.birthDate)

  const name = ref('')
  const gender = ref('')
  const birthDate = ref('')
  const isLunar = ref(false)
  const isLeapMonth = ref(false)
  const birthTime = ref('')
  const email = ref('')
  const submitted = ref(false)
  const saveAsMyInfo = ref(false)
  const loading = ref(false)
  const mode = ref(hasBirth.value ? 'my' : 'new')
  const isBodyTypeLoading = ref(false)

  const nameError = ref(false)
  const genderError = ref(false)
  const birthDateError = ref(false)
  const birthTimeError = ref(false)
  const emailError = ref(false)


  onMounted(async () => {
  })

  // 'Lydia' 부족 팝업 상태
  const showLydiaShortageModal = ref(false)

  // 모달 확인 버튼 클릭 핸들러
  const handleModalConfirm = () => {
    router.go(-1)
  }
  // 모달 충전 버튼 클릭 핸들러
  const handleModalCharge = () => {
    router.replace('/ChargeLydia')
  }

  const goBack = () => {
    router.go(-1)
  }

  const validateName = () => {
    nameError.value = ''
    const nameRegex = /^[가-힣a-zA-Z]+$/
    if (!name.value) {
      return
    }
    if (!nameRegex.test(name.value)) {
      nameError.value = '한글과 알파벳만 입력 가능합니다.'
    }
  }

  const validateGender = () => {
    genderError.value = false
    if (!gender.value) {
      genderError.value = true
    }
  }

  const validateBirthDate = () => {
    birthDateError.value = ''
    const date = birthDate.value.replace(/\D/g, '')
    if (date.length === 8) {
      const year = parseInt(date.substring(0, 4), 10)
      const month = parseInt(date.substring(4, 6), 10)
      const day = parseInt(date.substring(6, 8), 10)
      const currentYear = new Date().getFullYear()
      if (
        year < 1945 ||
        year > currentYear ||
        month < 1 ||
        month > 12 ||
        day < 1 ||
        day > 31
      ) {
        birthDateError.value = '올바른 생년월일을 입력해 주세요.'
      }
    }
  }

  const validateCalendarType = () => {
    // 양력은 기본값이라 따로 에러 필요 없음
    // 다만 음력이 선택된 경우에만 윤달 여부 고려
    if (!isLunar.value) {
      isLeapMonth.value = false // 음력 해제 시 윤달도 자동 해제
    }
  }

  const validateEmail = () => {
    emailError.value = ''
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (email.value && !emailRegex.test(email.value)) {
      emailError.value = '올바른 이메일 형식이 아닙니다.'
    }
  }

  const showRecoveryCodeModal = ref(false)
  const recoveryCode = ref(null)
  const showBodyType = ref(false)
  const bodyTypeResult = ref('')
  const welcomeLydia = Number(import.meta.env.VITE_WELCOME_LYDIA)
  const apiEndpoint = import.meta.env.VITE_API_ENDPOINT
  const client = new GraphQLClient(apiEndpoint)

  const timeOptions = Array.from(
    { length: 24 },
    (_, i) =>
      `${String(i).padStart(2, '0')}~${String((i + 1) % 24).padStart(2, '0')}`,
  )

  // analyzeGanji API 호출 쿼리 정의 및 함수 (기존 로직 유지)
  const ANALYZE_GANJI_ELEMENTS_QUERY = gql`
    query AnalyzeGanjiElements($input: AnalyzeGanjiInput!) {
      analyzeGanjiElements(input: $input) {
        yearGanji
        monthGanji
        dayGanji
        hourGanji
        elementCounts {
          wood
          fire
          earth
          metal
          water
        }
      }
    }
  `

  const analyzeGanjiElements = async (
    birth,
    hourRange
  ) => {
    const variables = {
      input: {
        birth,
        hourRange,
        isLunar: isLunar.value,
        isLeapMonth: isLeapMonth.value,
      },
    }
    try {
      const data = await client.request(ANALYZE_GANJI_ELEMENTS_QUERY, variables)
      return data.analyzeGanjiElements
    } catch (error) {
      console.error('Error calling analyzeGanjiElements API:', error)
      throw new Error('간지 분석 중 오류가 발생했습니다.')
    }
  }

  const handleSubmit = async () => {
    submitted.value = true

    if (mode.value === 'my') {
      navigateToResult()
      return
    }

    validateName()
    validateGender()
    validateBirthDate()
    validateCalendarType()
    validateEmail()

    if (
      !gender.value ||
      !birthDate.value ||
      birthDateError.value ||
      !birthTime.value
    )
    return

    loading.value = true
    try {
      const birth = birthDate.value
      const hourRange = birthTime.value
      const result = await analyzeGanjiElements(birth, hourRange)
      console.log('간지 분석 결과:', result)

      const accessToken = localStorage.getItem('accessToken')
      const docRef = doc(db, 'users', accessToken)

      const userSnapshot = await store.dispatch('getUserSnapshot')
      const alreadyHasGanji = !!userSnapshot?.ganji

      const commonData = {
        gender: gender.value,
        birthDate: birth,
        isLunar: isLunar.value,
        isLeapMonth: isLeapMonth.value,
        birthTime: birthTime.value,
        ganji: {
          yearGanji: result.yearGanji,
          monthGanji: result.monthGanji,
          dayGanji: result.dayGanji,
          hourGanji: result.hourGanji,
        },
        elementCounts: result.elementCounts,
      }

      if (saveAsMyInfo.value) {
        const recovery = userSnapshot?.recoveryCode || generateCode(10)
        const reward = alreadyHasGanji ? 0 : welcomeLydia

        const userData = {
          name: name.value || userSnapshot?.name || '',
          email: email.value || userSnapshot?.email || '',
          ...commonData,
          lydia: (userSnapshot.lydia || 0) + reward,
          recoveryCode: recovery,
          createdAt: userSnapshot?.createdAt || new Date().toISOString(),
        }

        await setDoc(docRef, userData, { merge: true }) // Firestore에 저장
        await store.dispatch('initializeAuth')

        if (!alreadyHasGanji) {
          recoveryCode.value = recovery
          showRecoveryCodeModal.value = true
          return
        }
      } else {
        // Firestore에 쓰지 않고, store만 업데이트
        await store.commit('UPDATE_USER_DATA', commonData)
      }

      navigateToResult()
    } catch (e) {
      console.error('분석 실패:', e)
      showToast('분석 중 오류가 발생했습니다.')
    } finally {
      loading.value = false
    }
  }

  const navigateToResult = async () => {
    const from = route.query.from

    let canProceed = false;
    
    if (from === 'IntroDaily') {
      canProceed = await canUseThis('fiveElementDaily', store)
    } 
    else if (from === 'IntroFortune') { 
      canProceed = await canUseThis('fortuneThisYear', store)
    }
    else { // from === 'IntroMBTI'
      canProceed = await canUseThis('MBTI', store)
    }

    if (!canProceed) {
      showLydiaShortageModal.value = true
      console.log('사주 요구 서비스에 필요한 Lydia 잔액이 부족합니다.')
      return
    }

    if (from === 'IntroFortune') {
      router.replace('/FortuneThisYear')
    } else if (from === 'IntroDaily') {
      router.replace('/FiveElementDaily')
    }
    else { // from === 'IntroMBTI'
      router.replace('/MBTI')
    }
  }

  const startBodyTypeAnalysis = () => {
    isBodyTypeLoading.value = true
    setTimeout(() => {
      showBodyType.value = true
      isBodyTypeLoading.value = false
    }, 500)  
  }

  const onBodyTypeCalculated = async (value) => {
    bodyTypeResult.value = value
    showBodyType.value = false

    const accessToken = localStorage.getItem('accessToken')
    const docRef = doc(db, 'users', accessToken)

    try {
      await updateDoc(docRef, { bodyType: value })   // Firestore 반영
      await store.dispatch('initializeAuth')         // store 및 localStorage 동기화
    } catch (error) {
      console.error('체질 결과 저장 실패:', error)
      showToast('체질 정보를 저장하는 데 실패했습니다.')
    }
  }

  const closeRecoveryCodeModal = () => {
    showRecoveryCodeModal.value = false
    navigateToResult()
  }
</script>

<style scoped>
  .setup-container {
    padding: 1.0rem;
    min-height: 100vh;
    width: var(--device-width);
    max-width: 480px;
    margin: auto;
    background-color: #f5f5f5;
  }

  .page-title {
    font-size: var(--font-size-14);
    margin: 0.5rem 0 0.5rem 0; /* 위/아래 여백을 0.5rem로 모두 축소 */
    font-weight: bold;
    text-align: center;
    margin-bottom: 1.5rem;
  }

  .section {
    margin-bottom: 2rem;
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 1rem;
    gap: 1rem;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
    background: white;
  }

  .radio-label,
  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: bold;
    margin-bottom: 0.5rem;
  }

  .radio-label span {
    font-size: var(--font-size-11);
  }

  .info-box {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .my-info-box {
    display: flex;
    flex-direction: column;
  }

  .info-text {
    font-size: var(--font-size-10);
    color: #333;
  }

  .guide-text {
    font-size: var(--font-size-10);
    color: darkgray;
    text-align: center;
    margin-bottom: 1.2rem;
  }

  .input {
    width: 100%;
    padding: 0.6rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: var(--font-size-10);
  }

  .button-group {
    display: flex;
    justify-content: center;
    gap: 1.2rem;
    margin-top: 1rem;
  }

  .primary-button {
    padding: 0.7rem 1.5rem;
    font-size: var(--font-size-10);
    border: none;
    border-radius: 8px;
    background-color: #4caf50;
    color: white;
    cursor: pointer;
  }
  .primary-button:hover {
    background-color: #45a049;
  }

  .back-button {
    padding: 0.7rem 1.5rem;
    font-size: var(--font-size-10);
    border: none;
    border-radius: 8px;
    background-color: #f44336; /* Red */
    color: white;
    cursor: pointer;
  }

  .back-button:hover {
    background-color: #da190b; /* Darker red */
  }

  .menu-button {
    font-size: var(--font-size-095);
  }  

  /* 모달(팝업) 스타일 */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: var(--device-width);
    max-width: 480px;
    margin: auto;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000; /* 다른 요소 위에 표시 */
  }

  .modal-content {
    background-color: white;
    padding: 1rem;
    border-radius: 12px;
    text-align: center;
    width: 90%;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  }

  .modal-content h2 {
    margin-top: 0;
    margin-bottom: 1rem;
    font-size: var(--font-size-12);
    color: #333;
  }

  .modal-content p {
    margin-bottom: 1rem;
    font-size: var(--font-size-10);
    color: #555;
  }

  .modal-content .recovery-code {
    font-size: var(--font-size-11);
    font-weight: bold;
    color: #007bff; /* 강조 색상 */
    word-break: break-all;
    margin-bottom: 1.5rem;
    padding: 0.8rem;
    background-color: #e9ecef; /* 배경색 추가 */
    border-radius: 4px;
  }

  .modal-content button {
    background-color: #4caf50;
    color: white;
    padding: 0.7rem 1.5rem;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: var(--font-size-10);
    transition: background-color 0.3s ease;
  }

  .modal-content button:hover {
    background-color: #45a049;
  }

  .disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  .calendar-options {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    font-size: var(--font-size-10);
    margin-top: -0.5rem;
    margin-bottom: 1rem;
  }

  .gender-options {
    display: flex;
    justify-content: flex-end; /* calendar와 같은 배치 */
    gap: 1rem;
    font-size: var(--font-size-10);
    margin-top: -0.5rem;
    margin-bottom: 1rem;
  }

  .gender-label {
    display: flex;
    align-items: center;
    gap: 0.3rem;
  }

  .error-text {
    color: red;
    font-size: var(--font-size-09);
    margin-top: -0.5rem;
    margin-bottom: 0.5rem;
  }

  .hint {
    font-size: var(--font-size-09);
    color: #666;
    margin-top: 0.2rem;
  }

  .bodytype-wrapper {
    margin-top: 1rem;
  }
</style>
